import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import {
  FreightRoute,
  FreightRouteLeg,
  FreightRateHistory,
  CreateFreightRouteData,
  UpdateFreightRouteData,
  CreateFreightRouteLegData,
  UpdateFreightRouteLegData,
  IncotermConfig,
  UpsertIncotermConfigData,
  UpdateIncotermConfigData,
} from '../types/freight-route.types';

class FreightRouteService {
  // ============ Freight Routes ============

  async getRoutes(): Promise<FreightRoute[]> {
    return apiClient.get(ENDPOINTS.FREIGHT_ROUTES);
  }

  async getRoute(id: string): Promise<FreightRoute> {
    return apiClient.get(ENDPOINTS.FREIGHT_ROUTE_BY_ID(id));
  }

  async createRoute(routeData: CreateFreightRouteData): Promise<FreightRoute> {
    return apiClient.post(ENDPOINTS.FREIGHT_ROUTES, routeData);
  }

  async updateRoute(id: string, routeData: UpdateFreightRouteData): Promise<FreightRoute> {
    return apiClient.put(ENDPOINTS.FREIGHT_ROUTE_BY_ID(id), routeData);
  }

  async deleteRoute(id: string): Promise<void> {
    await apiClient.delete(ENDPOINTS.FREIGHT_ROUTE_BY_ID(id));
  }

  // ============ Freight Route Legs ============

  async addLeg(routeId: string, legData: CreateFreightRouteLegData): Promise<FreightRouteLeg> {
    return apiClient.post(ENDPOINTS.FREIGHT_ROUTE_LEGS(routeId), legData);
  }

  async updateLeg(
    routeId: string,
    legId: string,
    legData: UpdateFreightRouteLegData
  ): Promise<FreightRouteLeg> {
    return apiClient.put(ENDPOINTS.FREIGHT_ROUTE_LEG_BY_ID(routeId, legId), legData);
  }

  async deleteLeg(routeId: string, legId: string): Promise<void> {
    await apiClient.delete(ENDPOINTS.FREIGHT_ROUTE_LEG_BY_ID(routeId, legId));
  }

  async getLegRateHistory(routeId: string, legId: string): Promise<FreightRateHistory[]> {
    return apiClient.get(ENDPOINTS.FREIGHT_ROUTE_LEG_HISTORY(routeId, legId));
  }

  // ============ Incoterm Configs ============

  async getIncotermConfigs(): Promise<IncotermConfig[]> {
    return apiClient.get(ENDPOINTS.INCOTERM_CONFIGS);
  }

  async upsertIncotermConfig(configData: UpsertIncotermConfigData): Promise<IncotermConfig> {
    return apiClient.post(ENDPOINTS.INCOTERM_CONFIGS, configData);
  }

  async updateIncotermConfig(
    id: string,
    configData: UpdateIncotermConfigData
  ): Promise<IncotermConfig> {
    return apiClient.put(ENDPOINTS.INCOTERM_CONFIG_BY_ID(id), configData);
  }

  async seedIncotermDefaults(): Promise<{ message: string; configs: IncotermConfig[] }> {
    return apiClient.post(ENDPOINTS.INCOTERM_CONFIGS_SEED);
  }
}

export const freightRouteService = new FreightRouteService();
